// ─── Types frontend — miroir des DTOs backend ────────────────────────────────

export type RiskLevel = 'OK' | 'WARNING' | 'CRITICAL'
export type Action    = 'PASS' | 'REVIEW' | 'SUSPEND' | 'BLOCK' | 'TAKEDOWN'
export type CountryCode = 'SN' | 'CI' | 'ML' | 'BF' | 'GN' | 'TG' | 'BJ' | 'NE' | 'CM' | 'GH' | 'FR' | 'BE' | 'CA' | 'GA'

export interface DashboardStats {
  activeMerchants: number
  newMerchantsThisMonth: number
  openAlerts: number
  alertsDeltaVsYesterday: number
  crawlsLast24h: number
  crawlSuccessRate: number
  suspendedMerchants: number
  lastCrawlAt: string
}

export interface AlertTimeSeries {
  date: string
  critical: number
  warning: number
  ok: number
}

export interface ActionDistribution {
  action: string
  count: number
  percentage: number
}

export interface CrawlReportSummary {
  id: string
  merchantId: string
  merchantName: string
  url: string
  country: CountryCode
  countryFlag: string
  category: string
  httpStatus: number
  sslValid: boolean
  riskScore: number
  riskLevel: RiskLevel
  action: Action
  driftScore: number | null
  prohibitedActivityDetected: boolean
  usurpationDetected: boolean
  screenshotUrl: string | null
  error: string | null
  crawledAt: string
  crawledAgo: string
}

export interface CrawlReportDetail extends CrawlReportSummary {
  finalUrl: string
  responseTimeMs: number
  sslExpiry: string | null
  contentHash: string | null
  paymentKeywordsFound: string[]
  reasons: string[]
  scores: { availability: number; regulatory: number; vision: number }
  visibleTextSnippet: string
  prohibitedActivityDetails: string | null
  aiSummary: string | null
}

export interface SuspiciousDomain {
  id: string
  domain: string
  matchedBrand: string
  matchType: string
  source: string
  suspicionLevel: string
  similarityScore: number
  isActive: boolean | null
  screenshotUrl: string | null
  riskScore: number | null
  action: string | null
  notes: string
  detectedAt: string
  detectedAgo: string

  // Traitement
  triageStatus?: TriageStatus
  assignedTo?: string | null
  resolved?: boolean
}

export interface CountryRuleRow {
  activity: string
  activityLabel: string
  rules: Partial<Record<CountryCode, string>>
}

export interface QueueStatsData {
  waiting: number
  active: number
  completed: number
  failed: number
  delayed: number
}

export interface MonitoredBrand {
  id: string
  name: string
  description: string | null
  searchTerms: string[]
  officialDomains: string[]
  customTyposquats: string[]
  paymentSignatures: string[]
  targetCountries: string[]
  /** Formulations de recherche web N4 — variables {brand} et {country} */
  searchPatterns: string[]
  isActive: boolean
  category: 'INTERNAL' | 'PARTNER' | 'CLIENT'
  displayColor: string | null
  lastScannedAt: string | null
  createdAt: string
  updatedAt: string
}

export interface BrandScanResult {
  brandId: string
  brandName: string
  total: number
  saved: number
  domains: {
    domain: string
    suspicionLevel: string
    matchType: string
    similarityScore: number
  }[]
  scannedAt: string
}

export type ComplianceStatus = 'COMPLIANT' | 'NON_COMPLIANT' | 'UNDECLARED' | 'PENDING_REVIEW'
export type MerchantMatchStatus = 'KNOWN_MERCHANT' | 'UNDECLARED' | 'PARTIAL_MATCH'

export interface PaymentIntegration {
  id: string
  domain: string
  url: string
  matchedBrand: string
  matchedSignatures: string[]
  merchantMatchStatus: MerchantMatchStatus
  matchedMerchantId: string | null
  complianceStatus: ComplianceStatus
  detectedActivity: string | null
  declaredActivity: string | null
  riskScore: number
  action: string | null
  pageTitle: string | null
  snippet: string | null
  country: string | null
  isActive: boolean | null
  screenshotUrl: string | null
  detectedAt: string
  detectedAgo: string
}

// ─── Scan mondial N4 (WebScan) ───────────────────────────────────────────────

export type WebScanAction = 'ONBOARD' | 'INVESTIGATE' | 'BLOCK'
export type DiscoveryMethod = 'SEARCH_WORLDWIDE' | 'SEARCH_COUNTRY' | 'RECURSIVE_CRAWL'

export type MatchResult =
  | { type: 'UNKNOWN_SITE' }
  | { type: 'KNOWN_AUTHORIZED'; merchantId: string; merchantName: string }
  | { type: 'KNOWN_UNAUTHORIZED'; merchantId: string; merchantName: string; authorizedBrands: string[] }

export interface UnauthorizedMerchantDto {
  id: string
  url: string
  finalUrl: string
  title: string
  country: string | null
  searchedBrand: string
  searchedBrandId: string
  paymentMethodsFound: string[]
  discoveryMethod: DiscoveryMethod
  sourceQuery: string
  matchResult: MatchResult
  isKnownMerchant: boolean
  detectedCategory: string | null
  riskScore: number
  action: WebScanAction
  textSnippet: string
  screenshotKey: string | null

  // L'API sert l'entité telle quelle : le rapprochement arrive à plat, pas
  // sous forme d'objet `matchResult`.
  matchType?: MatchResult['type']
  matchedMerchantId?: string | null
  matchedMerchantName?: string | null
  /** Marques pour lesquelles ce marchand EST autorisé — le contraste utile. */
  authorizedBrands?: string[]

  // Traitement
  triageStatus?: TriageStatus
  assignedTo?: string | null
  resolved?: boolean
}

export interface WebScanQuery {
  country?: string
  brand?: string
  action?: string
  page?: number
  limit?: number
}

/**
 * Capacité réelle du scan N4 à distinguer un marchand autorisé d'un marchand
 * non autorisé. Sans autorisation marchand × marque enregistrée, le croisement
 * est inerte et les résultats ne sont que des pistes à qualifier.
 */
// ─── Traitement des détections ───────────────────────────────────────────────

export type TriageStatus =
  | 'NEW'
  | 'TO_REVIEW'
  | 'IN_REVIEW'
  | 'CONFIRMED'
  | 'FALSE_POSITIVE'
  | 'ESCALATED'
  | 'ARCHIVED'

/** Familles de détection partageant la même file de traitement. */
export type TriageKind = 'webscan' | 'discovery' | 'betting'

export interface TriageLogEntry {
  from: TriageStatus
  to: TriageStatus
  comment: string
  agent: string
  at: string
}

/** Vocabulaire servi par l'API : l'interface ne code aucun statut en dur. */
export interface TriageVocabulary {
  statuses: Array<{
    value: TriageStatus
    label: string
    allowedNext: TriageStatus[]
    commentRequired: boolean
  }>
  overdueAfterDays: number
}

export interface TriageSummary {
  byStatus: Partial<Record<TriageStatus, number>>
  unassigned: number
  overdue: number
  total: number
}

/**
 * Statuts du circuit de traitement, dans l'ordre de lecture.
 *
 * Les libelles sont dans le catalogue de traduction (`triage.<STATUT>`) : une
 * valeur technique n'est pas du texte d'interface.
 */
export const TRIAGE_STATUSES: readonly TriageStatus[] = [
  'NEW', 'TO_REVIEW', 'IN_REVIEW', 'CONFIRMED', 'FALSE_POSITIVE', 'ESCALATED', 'ARCHIVED',
]

// ─── Rapports ────────────────────────────────────────────────────────────────

interface DetectionBlock {
  detected: number
  byStatus: Partial<Record<TriageStatus, number>>
  closed: number
  stillOpen: number
}

/** Bilan mensuel de surveillance, opposable à un tiers. */
export interface PortfolioReport {
  month: string
  periodStart: string
  periodEnd: string
  generatedAt: string
  scope: string[] | null
  merchants: {
    total: number
    active: number
    suspended: number
    neverCrawledInPeriod: number
  }
  crawls: {
    total: number
    byRiskLevel: Record<string, number>
    failed: number
  }
  detections: {
    brandAbuse: DetectionBlock
    undeclaredMerchants: DetectionBlock
  }
  overdue: {
    brandAbuse: number
    undeclaredMerchants: number
  }
}

/** Une ligne du fichier d'autorisations marchand × marque. */
export interface BrandAuthImportRow {
  merchant: string
  brand: string
  status?: BrandAuthStatus
  contractRef?: string
}

export interface BrandAuthImportReport {
  dryRun: boolean
  created: number
  updated: number
  unchanged: number
  errors: Array<{ row: number; label: string; error: string }>
}

export interface WebScanCoverage {
  merchants: number
  merchantsWithRootDomain: number
  activeBrands: number
  activeAuthorizations: number
  canDiscriminate: boolean
  warning: string | null
}

export interface PaginatedResponse<T> {
  data: T[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export interface ReportsQuery {
  level?: RiskLevel
  country?: CountryCode
  action?: Action
  page?: number
  limit?: number
  sortBy?: 'crawledAt' | 'riskScore'
  sortOrder?: 'asc' | 'desc'
}

export type DiscoverySource = 'CRT_SH' | 'APP_STORE' | 'SOCIAL' | 'SMISHING' | 'PAYMENT_INTEGRATION'

export interface SmishingReport {
  id: string
  reporterPhoneHash: string
  senderId: string
  smsBody: string
  extractedUrls: string[]
  senderIdSpoofed: boolean
  spoofedBrand: string | null
  classification: string
  riskScore: number
  suspicionLevel: string
  correlatedWithDiscovery: boolean
  correlatedDomainId: string | null
  status: 'PENDING' | 'CONFIRMED_SMISHING' | 'FALSE_POSITIVE' | 'ESCALATED'
  country: string | null
  reportedAt: string
  processedAt: string | null
}

export type MerchantStatus = 'ACTIVE' | 'SUSPENDED' | 'UNDER_REVIEW' | 'TERMINATED'
export type MerchantCategory = 'retail' | 'gaming' | 'adult' | 'crypto' | 'food' | 'travel' | 'telecom' | 'finance' | 'education' | 'health' | 'other'

export interface MerchantRow {
  id: string
  name: string
  url: string
  country: CountryCode
  category: MerchantCategory
  status: MerchantStatus
  crawlFrequencyHours: number
  baselineScreenshotHash: string | null
  baselineContentHash: string | null
  baselineKeywords: string[]
  lastCrawledAt: string | null
  createdAt: string
  updatedAt: string
}

export interface ImportResult {
  imported: number
  errors: Array<{ row: number; name: string; error: string }>
}

// ─── Autorisations marque × marchand ─────────────────────────────────────────

export type BrandAuthStatus = 'ACTIVE' | 'SUSPENDED' | 'PENDING' | 'REVOKED'

export interface MerchantBrandAuthRow {
  id: string
  merchantId: string
  brandId: string
  status: BrandAuthStatus
  contractRef: string | null
  notes: string | null
  authorizedAt: string
  revokedAt: string | null
  createdAt: string
  updatedAt: string
  /** Charge via relation — present quand on requete depuis /brands/:id/merchants */
  merchant?: MerchantRow
  /** Charge via relation — present quand on requete depuis /merchants/:id/brands */
  brand?: MonitoredBrand
}

/** Pays couverts. Les noms sont dans le catalogue (`country.<CODE>`). */
export const COUNTRY_CODES: readonly CountryCode[] = [
  'SN', 'CI', 'ML', 'BF', 'GN', 'TG', 'BJ', 'NE', 'CM', 'GH', 'FR', 'BE', 'CA', 'GA',
]

/** Categories declarables par un marchand (`category.<cle>` au catalogue). */
export const MERCHANT_CATEGORIES: readonly MerchantCategory[] = [
  'retail', 'telecom', 'finance', 'food', 'health', 'education', 'travel',
  'gaming', 'crypto', 'adult', 'other',
]

/** Statuts d'un marchand (`merchantStatus.<CODE>` au catalogue). */
export const MERCHANT_STATUSES: readonly MerchantStatus[] = [
  'ACTIVE', 'SUSPENDED', 'UNDER_REVIEW', 'TERMINATED',
]

export interface DiscoveryQuery {
  suspicionLevel?: string
  brand?: string
  activeOnly?: boolean
  source?: DiscoverySource
  page?: number
  limit?: number
}

// ─── Ad Detection (controle fiscal des annonceurs) ──────────────────────────

export type AdPlatform = 'META' | 'GOOGLE' | 'DISPLAY_NETWORK'
export type AdTypeName = 'SOCIAL' | 'SEARCH' | 'DISPLAY' | 'VIDEO' | 'UNKNOWN'
export type FiscalStatus = 'UNKNOWN' | 'REGISTERED' | 'NOT_REGISTERED' | 'UNDER_REVIEW'

export type DetectionTypeName = 'SPONSORED' | 'ORGANIC' | 'BANNER'

export type VerificationStatus = 'NEW' | 'TO_REVIEW' | 'IN_REVIEW' | 'CONFIRMED' | 'FALSE_POSITIVE' | 'ESCALATED' | 'ARCHIVED'

export interface VerificationLogEntry {
  from: VerificationStatus
  to: VerificationStatus
  comment: string
  /** Auteur du changement (compte connecté) */
  agent: string
  /** Agent désigné pour prendre le cas en charge */
  assignedTo?: string
  at: string
}

export interface SampleAd {
  adArchiveId: string
  body: string | null
  title: string | null
  linkUrl: string | null
  imageUrl: string | null
  adLibraryUrl: string
}

export interface DetectedAdvertiserRow {
  id: string
  advertiserName: string
  platform: AdPlatform
  domain: string | null
  pageId: string | null
  country: string
  adType: AdTypeName
  adCount: number
  estimatedSpendLower: number | null
  estimatedSpendUpper: number | null
  impressionsLower: number | null
  impressionsUpper: number | null
  firstSeenAt: string
  lastSeenAt: string
  sampleAdText: string | null
  sampleAdUrl: string | null
  sourceUrl: string | null
  detectedCategory: string | null
  detectionType: DetectionTypeName | null
  sourceQuery: string | null
  snippet: string | null
  pageTitle: string | null
  screenshotPath: string | null
  sampleAds: SampleAd[]
  fiscalStatus: FiscalStatus
  fiscalNotes: string | null
  verificationStatus: VerificationStatus
  verificationLogs: VerificationLogEntry[]
  createdAt: string
  updatedAt: string
}

export interface AdDetectionStats {
  totalAdvertisers: number
  /** Vrais annonceurs, issus des blocs sponsorisés. */
  sponsoredAdvertisers: number
  /** Résultats organiques : pistes, pas des annonceurs. */
  organicResults: number
  byCategory: Record<string, number>
  byVerification: Record<string, number>
  newLast30Days: number
  byPlatform: Record<string, number>
  byCountry: Record<string, number>
  byFiscalStatus: Record<string, number>
  lastScanAt: string | null
}

export interface AdScanStatus {
  running: boolean
  countries: string[]
}

export type ScanHistoryStatus = 'SUCCESS' | 'PARTIAL' | 'FAILED'

export interface AdScanHistoryRow {
  id: string
  country: string
  status: ScanHistoryStatus
  metaCount: number
  googleCount: number
  bannerCount: number
  totalFound: number
  newAdvertisers: number
  persisted: number
  durationMs: number
  error: string | null
  scanLogs: ScanLogEntry[]
  createdAt: string
}

export interface AdEvolutionPoint {
  date: string
  country: string
  totalFound: number
  newAdvertisers: number
  cumulativeTotal: number
}

export interface AdDetectionConfig {
  /**
   * Collecte Meta disponible — elle passe par Apify.
   *
   * L'API Graph de Meta n'est plus interrogee : elle repond « API access blocked ».
   * Le champ s'appelait `metaAdsConfigured` du temps ou elle servait ; le serveur l'a
   * renomme en abandonnant l'Ad Library, mais pas le client, qui lisait donc un champ
   * absent — toujours `undefined`, donc toujours « non configure ».
   */
  metaAdsApifyConfigured: boolean
  /** Fournisseur effectif de la collecte Meta. */
  metaAdsSource: 'APIFY' | 'NONE'
  googleAdsConfigured: boolean
  bannerDetectionConfigured: boolean
  supportedCountries: Array<{ code: string; label: string }>
}

export type ScanTargetCategory = 'NEWS' | 'FORUM' | 'ECOMMERCE' | 'CLASSIFIEDS' | 'BLOG' | 'PORTAL' | 'OTHER'

export interface BannerScanTarget {
  id: string
  url: string
  country: string
  name: string
  category: ScanTargetCategory
  isActive: boolean
  lastScannedAt: string | null
  adNetworkCount: number
  detectedNetworks: string[]
  createdAt: string
  updatedAt: string
}

export interface BannerScanSiteResult {
  url: string
  domain: string
  title: string
  country: string
  adNetworks: string[]
  banners: Array<{
    selector: string
    adNetwork: string
    adSlotId: string | null
    width: number
    height: number
    position: string
    isVisible: boolean
    destinationUrl: string | null
    adText: string | null
    boundingRect: { x: number; y: number; width: number; height: number } | null
    screenshotFile: string | null
  }>
  adRequestCount: number
  screenshotFile: string | null
  error: string | null
  scanDurationMs: number
}

export interface ScanLogEntry {
  level: 'OK' | 'WARN' | 'ERROR'
  site: string
  url: string
  message: string
  detail?: string
}

export interface BannerScanResult {
  country: string
  totalSitesScanned: number
  totalSitesWithAds: number
  totalBannersDetected: number
  adNetworksFound: Record<string, number>
  newAdvertisers: number
  persisted: number
  durationMs: number
  sites: BannerScanSiteResult[]
  scanLogs?: ScanLogEntry[]
  aborted?: boolean
}

export interface BannerScanProgress {
  type: 'start' | 'site-start' | 'site-done' | 'done'
  country: string
  currentSite?: string
  currentSiteIndex?: number
  totalSites?: number
  bannersFound?: number
  error?: string | null
}

export interface BannerDetectionRow {
  id: string
  advertiserName: string
  advertiserDomain: string | null
  siteUrl: string
  siteDomain: string
  siteTitle: string | null
  country: string
  adNetwork: string | null
  platform: AdPlatform
  adSlotId: string | null
  selector: string | null
  width: number
  height: number
  position: string
  destinationUrl: string | null
  adText: string | null
  screenshotFile: string | null
  boundingRect: { x: number; y: number; width: number; height: number } | null
  visionAnalyzed: boolean
  visionAdvertiser: string | null
  visionCategory: string | null
  visionProhibited: boolean
  visionProhibitedDetails: string | null
  visionConfidence: number | null
  visionSummary: string | null
  visionRecommendation: string | null
  visionPaymentMethods: string[] | null
  detectedAt: string
}

export interface BannerDetectionStats {
  total: number
  bySite: Array<{ siteDomain: string; count: string; uniqueAdvertisers: string }>
  byAdvertiser: Array<{ advertiserName: string; count: string; sitesCount: string }>
}

export interface AdSearchQuery {
  id: string
  query: string
  category: string
  /** Pays où la requête est jouée — vide = tous les pays */
  countries: string[]
  isActive: boolean
  createdAt: string
  updatedAt: string
}

// ─── Auth / Users ────────────────────────────────────────────────────────────

export type UserRole = 'SUPERADMIN' | 'OWNER' | 'ADMIN' | 'AGENT'

export interface User {
  id: string
  email: string
  fullName: string
  role: UserRole
  organizationId: string | null
  orgCountries: string[]
  allowedCountries: string[]
  allowedTabs: string[]
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface LoginResponse {
  accessToken: string
  user: User
}

// ─── Organisation (multi-tenant) ─────────────────────────────────────────────

export type OrgPlan = 'FREE' | 'PRO' | 'ENTERPRISE'

export interface Organization {
  id: string
  name: string
  slug: string
  countries: string[]
  /** Modules consultables. Liste vide = aucun accès, voir PLAN_MULTICLIENT.md. */
  modules: string[]
  plan: OrgPlan
  maxUsers: number
  maxCountries: number
  contactEmail: string
  contactName: string | null
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface OrgStats {
  org: Organization
  userCount: number
  countryCount: number
  merchantCount: number
  crawlReportCount: number
  advertiserCount: number
  scanCount: number
}

export interface GlobalOrgStats {
  totalOrgs: number
  activeOrgs: number
  byPlan: Record<string, number>
  totalUsers: number
  totalCountries: number
}

// ─── Constantes UI ────────────────────────────────────────────────────────────

export const COUNTRY_FLAGS: Record<CountryCode, string> = {
  SN: '🇸🇳', CI: '🇨🇮', ML: '🇲🇱', BF: '🇧🇫',
  GN: '🇬🇳', TG: '🇹🇬', BJ: '🇧🇯', NE: '🇳🇪',
  CM: '🇨🇲', GH: '🇬🇭', FR: '🇫🇷', BE: '🇧🇪', CA: '🇨🇦', GA: '🇬🇦',
}

export const RISK_COLORS: Record<RiskLevel, string> = {
  OK:       'var(--ok-fg)',
  WARNING:  'var(--warn)',
  CRITICAL: 'var(--crit)',
}

export const ACTION_COLORS: Record<Action, string> = {
  PASS:     'var(--ok-fg)',
  REVIEW:   'var(--info)',
  SUSPEND:  'var(--warn)',
  BLOCK:    'var(--crit)',
  TAKEDOWN: '#7c2d12',
}

export function scoreColor(score: number): string {
  if (score >= 70) return RISK_COLORS.CRITICAL
  if (score >= 40) return RISK_COLORS.WARNING
  return RISK_COLORS.OK
}


// ─── Paris & jeux ─────────────────────────────────────────────────────────────

export type ComplianceSeverity = 'COMPLIANT' | 'TECHNICAL_ISSUE' | 'LOW' | 'MEDIUM' | 'HIGH'
export type BreachType =
  | 'UNAUTHORISED_AGGREGATOR'
  | 'PROHIBITED_SERVICE'
  | 'UNLICENSED_OPERATOR'
  | 'UNAUTHORISED_BRAND_USE'
  | 'NON_COMPLIANT_ADVERTISING'
  | 'TECHNICAL_ISSUE'
  | 'OTHER'
export type PaymentTestSource = 'AUTO' | 'MANUAL'
export type PaymentTestRunStatus = 'PENDING' | 'RUNNING' | 'COMPLETED' | 'BLOCKED' | 'GEO_BLOCKED' | 'FAILED'
export type JourneyDepth = 'METHOD_SELECTION' | 'PAYMENT_REQUEST'
export type AggregatorStatus = 'AUTHORIZED' | 'UNAUTHORIZED' | 'UNKNOWN'
export type CandidateKind = 'BENEFICIARY' | 'DOMAIN'

/** Sens du mouvement d'argent. Seul le depot est automatisable. */
export type TestDirection = 'DEPOSIT' | 'WITHDRAWAL'

export interface BettingVocabulary {
  countries: Array<{ code: string; label: string }>
  severities: Array<{ value: ComplianceSeverity; label: string }>
  breachTypes: Array<{ value: BreachType; label: string; description: string }>
  paymentMethods: Array<{ name: string; mobileMoney: boolean }>
  /** Le serveur autorise les sites factices : l'interface propose de les afficher. */
  fixturesEnabled?: boolean
}

export interface BettingService {
  id: string
  name: string
  operatorCompany: string | null
  country: string
  mainUrl: string
  mirrorUrls: string[]
  merchantId: string | null
  journeyHints: string[]
  journeyDepth: JourneyDepth
  paymentRequestAmount: number | null
  checkFrequencyDays: number
  lastRunAt: string | null
  isActive: boolean
  /** Service de démonstration, adossé à un site factice. */
  isFixture?: boolean
  notes: string | null
  accountCount?: number
  lastTest?: { id: string; testNumber: number; runStatus: PaymentTestRunStatus; severity: ComplianceSeverity | null; createdAt: string } | null
}

export interface BettingTestAccount {
  id: string
  serviceId: string
  label: string
  loginUrl: string | null
  /** Masqué par le serveur */
  username: string
  testPhoneNumber: string | null
  isActive: boolean
  lastUsedAt: string | null
  lastLoginResult: string | null
}

export interface JourneyStep {
  order: number
  label: string
  url: string | null
  evidenceKey: string | null
  capturedAt: string
  auto: boolean
}

export interface DetectedAggregator {
  aggregatorId: string | null
  name: string
  status: AggregatorStatus | null
  evidence: string
}

export interface PaymentTestRow {
  id: string
  testNumber: number
  serviceId: string
  serviceName: string
  operatorCompany: string | null
  country: string
  source: PaymentTestSource
  runStatus: PaymentTestRunStatus
  severity: ComplianceSeverity | null
  breachType: BreachType | null
  carrierName: string | null
  aggregators: DetectedAggregator[]
  triageStatus: TriageStatus
  assignedTo: string | null
  isFixture?: boolean
  createdAt: string
}

export interface PaymentTestDetail {
  id: string
  testNumber: number
  serviceId: string
  country: string
  source: PaymentTestSource
  runStatus: PaymentTestRunStatus
  runMessage: string | null
  depth: JourneyDepth | null
  loggedIn: boolean
  carrierId: string | null
  carrierName: string | null
  adChannel: string | null
  connection: string | null
  steps: JourneyStep[]
  visitedUrls: string[]
  paymentMethods: Array<{ name: string; evidence: string }>
  aggregators: DetectedAggregator[]
  thirdPartyDomains: string[]
  smsText: string | null
  beneficiaryName: string | null
  amount: string | null
  breachType: BreachType | null
  severity: ComplianceSeverity | null
  description: string | null
  assessedBy: string | null
  testedBy: string | null
  testedAt: string | null
  triageStatus: TriageStatus
  assignedTo: string | null
  isFixture?: boolean
  statusChangedAt?: string | null
  triageLog?: TriageLogEntry[]
  createdAt: string
  service: BettingService | null
}

export interface PaymentTestSummary {
  total: number
  open: number
  closed: number
  compliant: number
  notCompliant: number
  bySeverity: Record<ComplianceSeverity | 'UNQUALIFIED', number>
  byBreach: Partial<Record<BreachType, number>>
  pendingCandidates: number
  topAggregators: Array<{ aggregatorId: string | null; name: string; status: AggregatorStatus | null; tests: number; notCompliant: number }>
}

export interface BettingTestFilters {
  country?: string
  serviceId?: string
  severity?: ComplianceSeverity
  breachType?: BreachType
  triageStatus?: TriageStatus
  /** '1' = seulement les dossiers non clos. */
  open?: string
  /** '1' = inclure les services et tests de démonstration. */
  includeFixtures?: string
  source?: PaymentTestSource
  carrierId?: string
  aggregatorId?: string
  from?: string
  to?: string
  search?: string
  page?: number
  limit?: number
}

export interface Carrier {
  id: string
  name: string
  country: string
}

export interface PaymentAggregator {
  id: string
  name: string
  status: AggregatorStatus
  countries: string[]
  domains: string[]
  beneficiaryNames: string[]
  notes: string | null
}

/** Famille d'un terme de recherche de sites de paris. */
export type DiscoveryTermKind = 'QUERY' | 'BETTING_SIGNAL' | 'REVIEW_SIGNAL' | 'NOISE_DOMAIN'

export interface DiscoveryTerm {
  id: string
  kind: DiscoveryTermKind
  value: string
  /** Pays concernés. Liste vide = tous. */
  countries: string[]
  isActive: boolean
  notes: string | null
}

/** Origine d'un candidat de paris. Seule la recherche consomme le quota mensuel. */
export type BettingDiscoverySource = 'SEARCH' | 'ADS'

export interface DiscoveryCandidate {
  domain: string
  name: string
  mainUrl: string
  country: string
  /** Requetes qui ont fait remonter ce domaine. Plus il y en a, plus c'est net. */
  queries: string[]
  /** Modeles de requete dont sortent ces requetes — les termes du vocabulaire. */
  queryTerms: string[]
  /** Mots de reconnaissance qui ont fait retenir ce domaine. */
  signalTerms: string[]
  /** Mots de comparateur qui l'ont fait releguer, s'il l'a ete. */
  reviewTerms: string[]
  /** Annonceurs dont la publicite mene a ce domaine. */
  advertisers: string[]
  /** Extrait qui a motive la retenue, pour juger sans rouvrir le site. */
  evidence: string
  /** Comparateur ou site d'affiliation probable : une piste, pas un operateur. */
  likelyReviewSite: boolean
}

export interface DiscoveryReport {
  country: string
  /** Requetes reellement envoyees, donc consommees sur le quota. */
  queriesRun: string[]
  resultsSeen: number
  /** Domaines deja au catalogue, ecartes d'office. */
  knownDomains: number
  advertisersScanned: number
  candidates: DiscoveryCandidate[]
  /** Renseigne quand la decouverte n'a pas pu conclure. */
  warning?: string
}

/** Jugement rendu sur un candidat de la recherche de sites. */
export type DiscoveryCandidateStatus = 'PENDING' | 'IMPORTED' | 'REJECTED'

/**
 * Candidat consigne au journal, juge ou non.
 *
 * Les ecartes sont l'essentiel de ce journal : sans eux, aucun taux de reussite
 * n'a de denominateur.
 */
export interface LoggedDiscoveryCandidate {
  id: string
  domain: string
  country: string
  name: string
  mainUrl: string
  sources: BettingDiscoverySource[]
  queryTerms: string[]
  signalTerms: string[]
  reviewTerms: string[]
  advertisers: string[]
  evidence: string | null
  likelyReviewSite: boolean
  status: DiscoveryCandidateStatus
  decisionReason: string | null
  decidedBy: string | null
  decidedAt: string | null
  /** Nombre de passes qui ont remonte ce domaine. */
  occurrences: number
  lastSeenAt: string
  firstSeenAt: string
}

/** Rendement d'un terme du vocabulaire, mesure sur les candidats qu'il a remontes. */
export interface TermYield {
  kind: DiscoveryTermKind
  value: string
  /** null quand le terme a ete retire du vocabulaire depuis. */
  termId: string | null
  isActive: boolean | null
  surfaced: number
  imported: number
  rejected: number
  pending: number
  /** Candidats juges — seul denominateur honnete du taux. */
  decided: number
  /** null tant que rien n'a ete juge : un taux sur zero decision n'est pas une mesure. */
  successRate: number | null
  /** Rejets portant un motif ecrit. Un candidat retenu n'a pas de motif a donner. */
  rejectedWithReason: number
}

export interface VocabularyYield {
  country: string | null
  totals: {
    candidates: number
    pending: number
    imported: number
    rejected: number
    /** Rejets sans motif ecrit. Mesure la saisie, pas le vocabulaire. */
    rejectedWithoutReason: number
  }
  terms: TermYield[]
}

export type ImportAction = 'CREATE' | 'ADD_MIRROR' | 'SKIP' | 'REJECT'

export interface ImportEntry {
  name: string
  operatorCompany?: string | null
  country: string
  mainUrl: string
  mirrorUrls?: string[]
  notes?: string | null
}

export interface ImportDecision {
  entry: ImportEntry
  action: ImportAction
  /** Motif lisible, affiche tel quel. */
  reason: string
  serviceId?: string
  serviceName?: string
  newMirrors?: string[]
  domains: string[]
}

export interface AggregatorCandidate {
  id: string
  kind: CandidateKind
  value: string
  country: string
  status: 'PENDING' | 'LINKED' | 'IGNORED'
  occurrences: number
  isFixture?: boolean
  firstTestId: string | null
  createdAt: string
}
